import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

interface PreviewPanelProps {
  config: {
    companyName: string
    companyTagline: string
    primaryEmail: string
    phone: string
    primaryColor: string
    secondaryColor: string
    accentColor: string
  }
  logoUrl?: string
}

export function PreviewPanel({ config, logoUrl }: PreviewPanelProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Live Preview</CardTitle>
        <CardDescription>
          See how your branding will appear in security reports
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Report Header Preview */}
          <div 
            className="p-6 rounded-lg border-2"
            style={{ 
              backgroundColor: `${config.primaryColor}10`,
              borderColor: config.primaryColor
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                {logoUrl && (
                  <img
                    src={logoUrl}
                    alt="Company logo"
                    className="h-12 object-contain"
                  />
                )}
                <div>
                  <h2 
                    className="text-xl font-bold" 
                    style={{ color: config.primaryColor }}
                  >
                    {config.companyName || 'Your Company Name'}
                  </h2>
                  <p className="text-sm text-gray-600">Security Assessment Report</p>
                </div>
              </div>
              <Badge 
                style={{ 
                  backgroundColor: config.secondaryColor,
                  color: 'white'
                }}
              >
                Confidential
              </Badge>
            </div>
            
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Scan Date:</p>
                <p className="font-medium">January 15, 2024</p>
              </div>
              <div>
                <p className="text-gray-600">Target:</p>
                <p className="font-medium">https://example.com</p>
              </div>
              <div>
                <p className="text-gray-600">Security Score:</p>
                <p 
                  className="font-bold text-lg"
                  style={{ color: config.accentColor }}
                >
                  82/100
                </p>
              </div>
            </div>
          </div>

          {/* Sample Report Card */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 
                className="font-semibold"
                style={{ color: config.primaryColor }}
              >
                Vulnerability Summary
              </h3>
              <Badge 
                variant="outline"
                style={{ borderColor: config.secondaryColor, color: config.secondaryColor }}
              >
                3 Issues Found
              </Badge>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center p-2 bg-red-50 rounded border-l-4 border-red-400">
                <span className="text-sm">SQL Injection Vulnerability</span>
                <Badge className="bg-red-100 text-red-800">High</Badge>
              </div>
              <div className="flex justify-between items-center p-2 bg-yellow-50 rounded border-l-4 border-yellow-400">
                <span className="text-sm">Missing Security Headers</span>
                <Badge className="bg-yellow-100 text-yellow-800">Medium</Badge>
              </div>
              <div className="flex justify-between items-center p-2 bg-blue-50 rounded border-l-4 border-blue-400">
                <span className="text-sm">Outdated TLS Configuration</span>
                <Badge className="bg-blue-100 text-blue-800">Low</Badge>
              </div>
            </div>
          </div>

          {/* Color Palette Display */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-gray-700">Current Color Palette</h4>
            <div className="flex gap-3">
              <div className="text-center">
                <div 
                  className="w-12 h-12 rounded-full border shadow"
                  style={{ backgroundColor: config.primaryColor }}
                />
                <p className="text-xs mt-1 text-gray-600">Primary</p>
              </div>
              <div className="text-center">
                <div 
                  className="w-12 h-12 rounded-full border shadow"
                  style={{ backgroundColor: config.secondaryColor }}
                />
                <p className="text-xs mt-1 text-gray-600">Secondary</p>
              </div>
              <div className="text-center">
                <div 
                  className="w-12 h-12 rounded-full border shadow"
                  style={{ backgroundColor: config.accentColor }}
                />
                <p className="text-xs mt-1 text-gray-600">Accent</p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}